import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant, Button, Loading, RefreshIcon } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'
import type { MarketplaceListing, MarketplaceOffer, MarketplaceSale } from '@mprolab/sdk'
import MainWrapper from 'layouts/MainWrapper'
import { StyledBiennale } from 'components'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { readAllListings } from 'utils/marketplace/readListings'
import { readAllOffers, isExpired } from 'utils/marketplace/readOffers'
import OfferCard from './components/OfferCard'
import { readSales } from 'utils/marketplace/readSales'
import { useMarketplaceContext } from 'utils/marketplace/useMarketplaceContext'
import { useDisplayNames } from 'hooks/useDisplayName'
import ListingCard from './components/ListingCard'
import SaleCard from './components/SaleCard'

const StyledTitle = styled(StyledBiennale)`
  font-size: 32px;
`

/**
 * One row: the two filters on the left, refresh and the call to action on the
 * right. This replaced three stacked rows of labelled chips — four toggles do
 * not need a form's worth of furniture, and a wall of chips gives no sense of
 * which choice belongs to which question. Two named dropdowns do.
 */
const Toolbar = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 24px 0 20px;

  ${({ theme }) => theme.mediaQueries.tablet} {
    flex-wrap: wrap;
  }
`

const Filters = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

/** Refresh and the call to action, kept together on the right. */
const Actions = styled(Flex)`
  align-items: center;
  gap: 12px;
`

/** Same select the proposal form uses, sized for a toolbar rather than a field. */
const FilterSelect = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 13px;
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

/**
 * The service's own refresh control — icon only, spinning while it works, the
 * same shape as the one on the exchange screens so it reads as the same action.
 */
const RefreshWrapper = styled(Flex)<{ refresh: boolean }>`
  cursor: pointer;
  align-items: center;
  animation: ${({ refresh }) => (refresh ? 'rotation' : 'none')} 1s linear infinite;
  @keyframes rotation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(359deg);
    }
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
  width: 100%;
`

type CollectionFilter = string | 'all'
/** All = everything that ever happened; the rest narrow it. */
type StatusFilter = 'all' | 'active' | 'sold' | 'mine'
/** Direction of the card: a listing sells, an offer wants. */
type TypeFilter = 'all' | 'sale' | 'wanted'

const STATUSES_NEEDING_SALES: StatusFilter[] = ['all', 'sold', 'mine']

const Marketplace: React.FC = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const navigate = useNavigate()
  const { account } = useWeb3React()
  const ctx = useMarketplaceContext()
  const [searchParams, setSearchParams] = useSearchParams()

  const [listings, setListings] = useState<MarketplaceListing[]>([])
  const [offers, setOffers] = useState<MarketplaceOffer[]>([])
  const [sales, setSales] = useState<MarketplaceSale[]>([])

  /*
   * One name lookup for everything on screen, rather than one per card.
   * A wallet whose owner keeps their profile private resolves to the shortened
   * address it always showed — this is a display convenience, never a
   * dependency, and it must degrade to exactly the previous behaviour.
   */
  const { displayName } = useDisplayNames([
    ...listings.map((l) => l.seller),
    ...offers.map((o) => o.buyer),
    ...sales.flatMap((s) => [s.seller, s.buyer])
  ])
  const [loading, setLoading] = useState(false)
  const [salesLoading, setSalesLoading] = useState(false)
  const [salesTruncated, setSalesTruncated] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [collectionFilter, setCollectionFilter] = useState<CollectionFilter>(
    searchParams.get('collection')?.toLowerCase() ?? 'all'
  )
  // The user menu links here with ?filter=mine, so "My auctions" is one entry
  // in this filter rather than a second screen showing the same cards.
  const [status, setStatus] = useState<StatusFilter>(searchParams.get('filter') === 'mine' ? 'mine' : 'active')
  // URL-addressable like `mine` is, so a link can open the market already
  // showing one side of the book.
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(
    searchParams.get('type') === 'wanted' ? 'wanted' : searchParams.get('type') === 'sale' ? 'sale' : 'all'
  )

  const fetchListings = useCallback(async () => {
    if (!ctx.marketplaceAddress) {
      setListings([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const provider = getSimpleRpcProviderByChainId(ctx.chainId)
      const [nextListings, nextOffers] = await Promise.all([
        readAllListings({
          marketplaceAddress: ctx.marketplaceAddress,
          provider: provider as ethers.providers.Provider
        }),
        // Empty until the V2 upgrade lands — readAllOffers degrades to []
        // on the V1 contract by design.
        readAllOffers({
          marketplaceAddress: ctx.marketplaceAddress,
          provider: provider as ethers.providers.Provider
        })
      ])
      setListings(nextListings)
      setOffers(nextOffers)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [ctx.marketplaceAddress, ctx.chainId])

  // Sales come from `Bought` events, so they are read only when a filter
  // actually needs them — a log scan costs far more than the listings call.
  const fetchSales = useCallback(async () => {
    if (!ctx.marketplaceAddress) return
    setSalesLoading(true)
    try {
      const provider = getSimpleRpcProviderByChainId(ctx.chainId)
      const result = await readSales({
        marketplaceAddress: ctx.marketplaceAddress,
        provider: provider as ethers.providers.Provider,
        deployedAtBlock: ctx.deployedAtBlock
      })
      setSales(result.sales)
      setSalesTruncated(result.truncated)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSalesLoading(false)
    }
  }, [ctx.marketplaceAddress, ctx.chainId, ctx.deployedAtBlock])

  useEffect(() => {
    void fetchListings()
    // Re-fetch every 60s so newly-listed items show up without manual refresh.
    const handle = setInterval(() => void fetchListings(), 60_000)
    return () => clearInterval(handle)
  }, [fetchListings])

  useEffect(() => {
    if (STATUSES_NEEDING_SALES.includes(status) && sales.length === 0 && !salesLoading) {
      void fetchSales()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  const onStatusChange = useCallback(
    (next: StatusFilter) => {
      setStatus(next)
      // Keep the address bar honest, so "My auctions" can be linked to.
      if (next === 'mine') searchParams.set('filter', 'mine')
      else searchParams.delete('filter')
      setSearchParams(searchParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const mine = account?.toLowerCase()

  const visibleListings = useMemo(() => {
    if (status === 'sold' || typeFilter === 'wanted') return []
    return (
      listings
        .filter((l) => l.active)
        // An expired listing cannot settle; showing it as buyable would only
        // manufacture a revert. The contract treats it as stale (reason 2).
        .filter((l) => !isExpired(l.expiresAt))
        .filter((l) => (collectionFilter === 'all' ? true : l.nft === collectionFilter))
        .filter((l) => (status === 'mine' ? l.seller === mine : true))
        .sort((a, b) => b.createdAt - a.createdAt)
    )
  }, [listings, collectionFilter, status, mine, typeFilter])

  const visibleOffers = useMemo(() => {
    if (status === 'sold' || typeFilter === 'sale') return []
    return offers
      .filter((o) => o.active)
      .filter((o) => !isExpired(o.expiresAt))
      .filter((o) => (collectionFilter === 'all' ? true : o.nft === collectionFilter))
      .filter((o) => (status === 'mine' ? o.buyer === mine : true))
      .sort((a, b) => b.createdAt - a.createdAt)
  }, [offers, collectionFilter, status, mine, typeFilter])

  const visibleSales = useMemo(() => {
    if (status === 'active') return []
    return sales
      .filter((s) => (collectionFilter === 'all' ? true : s.nft === collectionFilter))
      .filter((s) => (status === 'mine' ? s.seller === mine || s.buyer === mine : true))
  }, [sales, collectionFilter, status, mine])

  const busy = loading || (STATUSES_NEEDING_SALES.includes(status) && salesLoading)
  const nothing = visibleListings.length === 0 && visibleOffers.length === 0 && visibleSales.length === 0

  if (!ctx.marketplaceAddress) {
    return (
      <MainWrapper>
        <Flex fullWidth flexDirection="column" mt="152px" mb="120px">
          <StyledTitle>{t('marketplace.title')}</StyledTitle>
          <Text variant={TextVariant.BODY_DEFAULT} color={colors.gray6} mt="16px">
            {t('marketplace.notConfigured')}
          </Text>
        </Flex>
      </MainWrapper>
    )
  }

  return (
    <MainWrapper>
      <Flex fullWidth flexDirection="column" mt="152px" mb="120px">
        <Flex flexDirection="column" gap="8px">
          <StyledTitle>{t('marketplace.title')}</StyledTitle>
          <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
            {t('marketplace.subtitle')}
          </Text>
        </Flex>

        <Toolbar>
          <Filters>
            <FilterSelect
              value={collectionFilter}
              onChange={(e) => setCollectionFilter(e.target.value)}
              aria-label={t('marketplace.collection')}
            >
              <option value="all">{t('marketplace.allCollections')}</option>
              {ctx.collections.map((c) => (
                <option key={c.nft} value={c.nft}>
                  {c.label}
                </option>
              ))}
            </FilterSelect>

            <FilterSelect
              value={typeFilter}
              onChange={(e) => {
                const next = e.target.value as TypeFilter
                setTypeFilter(next)
                if (next === 'all') searchParams.delete('type')
                else searchParams.set('type', next)
                setSearchParams(searchParams, { replace: true })
              }}
              aria-label={t('marketplace.typeFilter')}
            >
              <option value="all">{t('marketplace.typeAll')}</option>
              <option value="sale">{t('marketplace.typeForSale')}</option>
              <option value="wanted">{t('marketplace.typeWanted')}</option>
            </FilterSelect>

            <FilterSelect
              value={status}
              onChange={(e) => onStatusChange(e.target.value as StatusFilter)}
              aria-label={t('marketplace.show')}
            >
              <option value="all">{t('marketplace.filterAll')}</option>
              <option value="active">{t('marketplace.filterActive')}</option>
              <option value="sold">{t('marketplace.filterCompleted')}</option>
              {account ? <option value="mine">{t('marketplace.filterMine')}</option> : null}
            </FilterSelect>
          </Filters>

          <Actions>
            <RefreshWrapper
              refresh={busy}
              onClick={() => {
                void fetchListings()
                if (STATUSES_NEEDING_SALES.includes(status)) void fetchSales()
              }}
            >
              <RefreshIcon />
            </RefreshWrapper>

            {account ? (
              <>
                <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/marketplace/list')}>
                  {t('marketplace.forSaleButton')}
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/marketplace/offer')}>
                  {t('marketplace.wantedButton')}
                </Button>
              </>
            ) : null}
          </Actions>
        </Toolbar>

        {busy ? (
          <Flex fullWidth justifyContent="center" py="48px">
            <Loading />
          </Flex>
        ) : error ? (
          <Text color={colors.red}>{error}</Text>
        ) : nothing ? (
          <Text textAlign="center" color={colors.gray6} mt="40px">
            {status === 'sold' ? t('marketplace.emptySold') : t('marketplace.empty')}
          </Text>
        ) : (
          <>
            <Grid>
              {visibleListings.map((l) => (
                <ListingCard
                  key={`listing-${l.listingId}`}
                  listing={l}
                  collections={ctx.collections}
                  displayName={displayName}
                  onClick={() => navigate(`/marketplace/listings/${l.listingId}`)}
                />
              ))}
              {visibleOffers.map((o) => (
                <OfferCard
                  key={`offer-${o.offerId}`}
                  offer={o}
                  collections={ctx.collections}
                  displayName={displayName}
                  onClick={() => navigate(`/marketplace/offers/${o.offerId}`)}
                />
              ))}
              {visibleSales.map((s) => (
                <SaleCard
                  key={`sale-${s.txHash}-${s.source}-${s.sourceId}`}
                  sale={s}
                  collections={ctx.collections}
                  displayName={displayName}
                />
              ))}
            </Grid>
            {salesTruncated && status !== 'active' ? (
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6} mt="16px" textAlign="center">
                {t('marketplace.soldTruncated')}
              </Text>
            ) : null}
          </>
        )}
      </Flex>
    </MainWrapper>
  )
}

export default Marketplace
